import Button from '@/components/core/Button';
import ButtonIcon from '@/components/core/ButtonIcon';
import Dropdown from '@/components/core/Dropdown';
import { Box, Grid, Paper, Typography, FormControl, Select, MenuItem, ButtonBase } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@material-ui/icons';
import { addMonths, format, parse, startOfMonth, subMonths } from 'date-fns';
import React, { useCallback, useMemo, useState } from 'react';
import { PhysicianDropdown } from '../kiosk/schedule/Filters';

const MonthOnlySelector = ({ date, onSelect, close }) => {
  const classes = useStyles();
  const currentYear = date.getFullYear();
  const currentMonth = date.getMonth();

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);
  }, []);

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const handleMonthSelect = monthIndex => {
    setSelectedMonth(monthIndex);
    const newDate = startOfMonth(new Date(selectedYear, monthIndex, 1));
    onSelect(newDate);
    close();
  };

  const handleYearSelect = year => {
    setSelectedYear(year);
    setYearDropdownOpen(false);
  };

  return (
    <Paper className={classes.monthSelector} elevation={0}>
      <Box p={3}>
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            Select Month & Year
          </Typography>
          <Box position="relative">
            <ButtonBase onClick={() => setYearDropdownOpen(!yearDropdownOpen)} className={classes.yearSelector}>
              <Typography variant="body1">{selectedYear}</Typography>
              <Box ml={1} className={classes.yearArrow}>
                {yearDropdownOpen ? '▲' : '▼'}
              </Box>
            </ButtonBase>
            {yearDropdownOpen && (
              <Paper className={classes.yearDropdown} elevation={3}>
                {years.map(year => (
                  <ButtonBase key={year} onClick={() => handleYearSelect(year)} className={classes.yearOption}>
                    <Typography variant="body2">{year}</Typography>
                  </ButtonBase>
                ))}
              </Paper>
            )}
          </Box>
        </Box>
        <Grid container spacing={1}>
          {months.map((month, index) => {
            const isSelected = index === selectedMonth && selectedYear === currentYear;
            return (
              <Grid item xs={4} key={month}>
                <ButtonBase
                  onClick={() => handleMonthSelect(index)}
                  className={`${classes.monthButton} ${isSelected ? classes.selectedMonth : ''}`}
                >
                  <Typography variant="body2" className={classes.monthText}>
                    {month.substring(0, 3)}
                  </Typography>
                </ButtonBase>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Paper>
  );
};

export const MonthPicker = ({ date, setDate }) => {
  const classes = useStyles();

  const handleDateSelect = useCallback(
    newDate => {
      const firstDayOfMonth = startOfMonth(newDate);
      setDate(firstDayOfMonth);
    },
    [setDate]
  );

  const label = useMemo(() => format(date, 'MMMM YYYY'), [date]);

  return (
    <div className={classes.picker}>
      <ButtonIcon onClick={() => setDate(subMonths(date, 1))}>
        <KeyboardArrowLeft />
      </ButtonIcon>
      <div>
        <Dropdown
          contentComponent={MonthOnlySelector}
          contentProps={{ date, onSelect: handleDateSelect }}
          style={{ zIndex: 9999 }}
        >
          <Button variant="contained">{label}</Button>
        </Dropdown>
      </div>
      <ButtonIcon onClick={() => setDate(addMonths(date, 1))}>
        <KeyboardArrowRight />
      </ButtonIcon>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  picker: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
  },
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  monthSelector: {
    minWidth: 320,
    maxWidth: 400,
    position: 'relative',
    zIndex: theme.zIndex.modal + 1,
  },
  yearSelector: {
    width: '100%',
    height: 48,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0, 1.5),
    transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow'], {
      duration: theme.transitions.duration.short,
    }),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      borderColor: theme.palette.primary.main,
    },
    '&:focus': {
      outline: 'none',
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}33`,
    },
  },
  yearArrow: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    transition: theme.transitions.create('color', {
      duration: theme.transitions.duration.short,
    }),
    '$yearSelector:hover &': {
      color: theme.palette.primary.main,
    },
  },
  yearDropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 10000,
    maxHeight: 200,
    overflowY: 'auto',
    marginTop: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[3],
  },
  yearOption: {
    width: '100%',
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 0,
    transition: theme.transitions.create('background-color', {
      duration: theme.transitions.duration.short,
    }),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:focus': {
      outline: 'none',
      backgroundColor: theme.palette.action.selected,
    },
    '&:first-child': {
      borderTopLeftRadius: theme.shape.borderRadius,
      borderTopRightRadius: theme.shape.borderRadius,
    },
    '&:last-child': {
      borderBottomLeftRadius: theme.shape.borderRadius,
      borderBottomRightRadius: theme.shape.borderRadius,
    },
  },
  monthButton: {
    width: '100%',
    height: 48,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow'], {
      duration: theme.transitions.duration.short,
    }),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      borderColor: theme.palette.primary.main,
    },
    '&:focus': {
      outline: 'none',
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}33`,
    },
  },
  selectedMonth: {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      borderColor: theme.palette.primary.dark,
    },
    '& $monthText': {
      color: theme.palette.primary.contrastText,
      fontWeight: theme.typography.fontWeightMedium,
    },
  },
  monthText: {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightRegular,
    transition: theme.transitions.create('color', {
      duration: theme.transitions.duration.short,
    }),
  },
}));

const ActionBar = ({
  date,
  setDate,
  physician,
  setPhysician,
  colorMode,
  setColorMode,
  hidePhysicianFilter = false,
}) => {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      {!hidePhysicianFilter && (
        <div style={{ minWidth: 300 }}>
          <PhysicianDropdown onChange={setPhysician} value={physician} />
        </div>
      )}
      {setDate && <MonthPicker date={date} setDate={setDate} />}
    </div>
  );
};

export default ActionBar;
